import type { PackagingReport, ReportRow } from "./week39-report";
import { dayPlanFulfillment } from "../../../shared/manual-packaging/packaging-plan-fulfillment";
import type { PackagingViewRow } from "./week39-view";
import type { Week39PackagingLive } from "./week39-types";
import { productionLineFromDocument, UNMAPPED_PRODUCTION_LINE } from "./week39-production-lines";
import { isPackagingWorkOrderPending } from "../../../shared/manual-packaging/packaging-work-order";

/** Presentation of verified contributions only; never substitutes a subtotal for a full total. */
export function packagingProgressEvidence(report: PackagingReport, date: string) {
  const day = date ? report.daily.find(entry => entry.date === date) : null;
  const observed = date ? day?.availability === "observed" : report.meta.range !== null
    && report.meta.range.from <= report.meta.weekStart && report.meta.range.to >= report.meta.weekStart;
  const samples = new Map(day?.samples.map(sample => [sample.sourceRow, sample]) ?? []);
  const rows = report.rows.filter(row => !date || samples.get(row.plan.sourceRow)?.relevant || samples.get(row.plan.sourceRow)?.planned);
  const actuals = observed ? rows.filter(row => !isPackagingWorkOrderPending(row.plan))
    .filter(row => !date || samples.get(row.plan.sourceRow)?.relevant)
    .map(row => date ? samples.get(row.plan.sourceRow)?.actual.value : row.weeklyActual.value)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value)) : [];
  const issues = observed ? rows.flatMap(row => {
    const sample = samples.get(row.plan.sourceRow);
    const pending = isPackagingWorkOrderPending(row.plan);
    const values = pending ? [date && sample ? sample.plan : row.weekPlan] : date && sample ? [sample.plan, sample.actual,
      ...(sample.planned ? [dayPlanFulfillment(row.plan, date, row.weeklyActual)] : [])]
      : [row.weekPlan, row.weeklyActual, row.remaining];
    const reasons = [...new Set([...(pending ? ["İş emri bekleniyor; hedef plana dahil, üretime dahil değil"] : []),
      ...values.filter(value => value.value === null || !Number.isFinite(value.value))
        .map(value => value.reason ?? "Miktar doğrulanamadı")])];
    return reasons.length ? [{ row, reason: reasons.join(" · ") }] : [];
  }) : [];
  return { verifiedActual: actuals.length ? actuals.reduce((total, value) => total + value, 0) : null,
    verifiedCount: actuals.length, issues };
}

export function pendingProgressNotice(report: PackagingReport, date: string) {
  if (!report.meta.range) return null;
  const unknown = report.rows.filter(row => row.weeklyActual.value === null || row.remaining.value === null);
  if (!unknown.length || unknown.some(row => !isPackagingWorkOrderPending(row.plan) || row.weekPlan.value === null)) return null;
  const samples = report.daily.find(day => day.date === date)?.samples ?? [];
  const pending = unknown.flatMap(row => {
    const sample = samples.find(item => item.sourceRow === row.plan.sourceRow && item.planned);
    const quantity = date ? sample?.plan.value : row.weekPlan.value;
    return quantity === null || quantity === undefined ? [] : [quantity];
  });
  return pending.length ? { count: pending.length, quantity: pending.reduce((total, quantity) => total + quantity, 0) } : null;
}

/** Use the entry timestamp, not the document date; retain the selected production scope. */
export function latestProductionInput(live: Week39PackagingLive | null, row: PackagingViewRow, date: string): string | null {
  const times = (live?.items ?? []).filter((item) => item.workOrderNo === row.plan.workOrderNo && item.productCode === row.plan.productCode
    && (!date || item.date === date) && (!row.plan.productionLine || (productionLineFromDocument(item.documentNo) ?? UNMAPPED_PRODUCTION_LINE) === row.plan.productionLine))
    .flatMap((item) => { const match = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})/u.exec(item.recordedAt ?? ""); return match ? [`${match[1]} ${match[2]}`] : []; });
  return times.sort().at(-1) ?? null;
}

export type ProductionExplanation = { key: string; label: string; quantity: number; entries: Array<{ row: ReportRow; quantity: number }> };

/** Daily target exceptions are disjoint; early production does not cover another job's target. */
export function productionExplanations(report: PackagingReport, date: string): ProductionExplanation[] {
  if (packagingProgress(report, date).outsidePlan === null) return [];
  const groups: ProductionExplanation[] = [
    { key: "other-day", label: "Başka günlere planlanan işler", quantity: 0, entries: [] },
    { key: "excess", label: date ? "Günlük hedef üzerindeki üretim" : "Haftalık hedef üzerindeki üretim", quantity: 0, entries: [] },
    { key: "unplanned", label: "Excel planında bulunmayan işler", quantity: 0, entries: [] },
  ];
  const samples = date ? report.daily.find((day) => day.date === date)?.samples ?? []
    : report.rows.map((row) => ({ sourceRow: row.plan.sourceRow, relevant: true, plan: row.weekPlan, actual: row.weeklyActual }));
  const rows = new Map(report.rows.map((row) => [row.plan.sourceRow, row]));
  for (const sample of samples) {
    const row = rows.get(sample.sourceRow);
    if (!row || !sample.relevant || sample.actual.value === null || sample.actual.value <= 0) continue;
    const unplanned = row.plan.unplanned;
    if (!unplanned && sample.plan.value === null) continue;
    const quantity = unplanned ? sample.actual.value : Math.max(0, sample.actual.value - (sample.plan.value ?? 0));
    if (!quantity) continue;
    const group = groups[unplanned ? 2 : date && sample.plan.value === 0 ? 0 : 1];
    group.quantity += quantity;
    group.entries.push({ row, quantity });
  }
  return groups.filter((group) => group.entries.length > 0);
}

export type PackagingProgress = {
  plan: number | null; actual: number | null; fulfilled: number | null; remaining: number | null;
  percentage: number | null; actualPercentage: number | null; outsidePlan: number | null; partialDay: boolean;
  state: "observed" | "future" | "unavailable" | "empty";
};
const sum = (values: Array<number | null>) => values.some((value) => value === null) ? null : values.reduce<number>((total, value) => total + (value ?? 0), 0);
const finish = (plan: number | null, actual: number | null, fulfilled: number | null) => ({
  plan, actual, fulfilled,
  remaining: plan === null || fulfilled === null ? null : Math.max(0, plan - fulfilled),
  percentage: plan === null || plan <= 0 || fulfilled === null ? null : Math.round(fulfilled / plan * 100),
  actualPercentage: plan === null || plan <= 0 || actual === null ? null : Math.round(actual / plan * 100),
  outsidePlan: actual === null || fulfilled === null ? null : Math.max(0, actual - fulfilled),
});

/** Scope is the full filtered report, before the completed-row visibility switch. */
export function packagingProgress(report: PackagingReport, date: string): PackagingProgress {
  if (!date) {
    const { plan, actual, remaining } = report.summary;
    const observed = report.meta.range !== null && report.meta.range.from <= report.meta.weekStart
      && report.meta.range.to >= report.meta.weekStart;
    const fulfilled = !observed || plan.value === null || remaining.value === null ? null : Math.max(0, plan.value - remaining.value);
    const actualValue = observed && actual.count === 0 && report.rows.length ? 0 : actual.value;
    return { ...finish(plan.value, actualValue, fulfilled), partialDay: false,
      state: !report.rows.length ? "empty" : report.meta.range ? "observed" : "unavailable" };
  }
  const day = report.daily.find((entry) => entry.date === date);
  if (!day) return { ...finish(0, null, null), partialDay: false, state: "empty" };
  const plan = sum(day.samples.map((sample) => sample.plan.value));
  const observed = day.availability === "observed";
  const actual = observed ? sum(day.samples.filter((sample) => sample.relevant).map((sample) => sample.actual.value)) : null;
  const sameDayFulfilled = observed ? sum(day.samples.map((sample) => sample.plan.value === 0 ? 0
    : sample.plan.value === null || sample.actual.value === null ? null : Math.min(sample.plan.value, Math.max(0, sample.actual.value)))) : null;
  const rows = new Map(report.rows.map((row) => [row.plan.sourceRow, row]));
  const fulfilled = observed ? sum(day.samples.map((sample) => {
    const row = rows.get(sample.sourceRow);
    return sample.plan.value === null ? null : sample.plan.value === 0 ? 0
      : row && isPackagingWorkOrderPending(row.plan) ? 0 : row ? dayPlanFulfillment(row.plan, date, row.weeklyActual).value : null;
  })) : null;
  return { ...finish(plan, actual, fulfilled), outsidePlan: actual === null || sameDayFulfilled === null ? null : Math.max(0, actual - sameDayFulfilled), partialDay: day.isPartialDay,
    state: day.availability === "future" ? "future" : day.availability === "unavailable" ? "unavailable" : day.samples.length ? "observed" : "empty" };
}
